import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authClient } from "@/lib/auth/client";

export function InviteTeammate() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const client = authClient as unknown as {
        organization: {
          inviteMember: (args: {
            email: string;
            role: "member" | "admin";
          }) => Promise<{ error?: { message?: string } }>;
        };
      };
      const result = await client.organization.inviteMember({ email, role });
      if (result?.error) {
        throw new Error(result.error.message ?? "Failed to send invite");
      }
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-card p-4 sm:p-6">
      <div>
        <h3 className="text-base font-semibold">Invite a teammate</h3>
        <p className="text-sm text-muted-foreground">
          They'll receive an email with a link to join your workspace.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@brokerage.com"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-role">Workspace role</Label>
          <Select value={role} onValueChange={(v) => setRole(v as "member" | "admin")}>
            <SelectTrigger id="invite-role" className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member · view & edit</SelectItem>
              <SelectItem value="admin">Admin · also invite</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? "Sending…" : "Send invite"}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Workspace roles control what teammates can do inside this workspace. They are separate from
        Flotion platform staff access.
      </p>
    </form>
  );
}
